package com.alpha.showcase.common.ui.settings

import com.alpha.showcase.common.ui.ai.AI_IMAGE_SUMMARY_KEY
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.Sort
import androidx.compose.material.icons.outlined.AccessTime
import androidx.compose.material.icons.outlined.HistoryToggleOff
import androidx.compose.material.icons.outlined.Style
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.alpha.showcase.common.theme.AppThemeStyle
import com.alpha.showcase.common.ai.aiFeaturesAvailable
import com.alpha.showcase.common.sync.ConfigSync
import com.alpha.showcase.common.ui.ai.AiClientSettings
import com.alpha.showcase.common.ui.play.DEFAULT_PERIOD
import com.alpha.showcase.common.ui.settings.DarkThemePreference.Companion.FOLLOW_SYSTEM
import showcaseapp.composeapp.generated.resources.Res
import com.alpha.showcase.common.ui.view.CheckItem
import com.alpha.showcase.common.ui.view.SlideItem
import com.alpha.showcase.common.ui.view.SwitchItem
import com.alpha.showcase.common.ui.view.TextTitleMedium
import com.alpha.showcase.common.utils.SYSTEM_DEFAULT
import isWeb
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import showcaseapp.composeapp.generated.resources.about
import showcaseapp.composeapp.generated.resources.ai_configuration_title
import showcaseapp.composeapp.generated.resources.cloud_sync_title
import showcaseapp.composeapp.generated.resources.display_style_calender
import showcaseapp.composeapp.generated.resources.display_style_carousel
import showcaseapp.composeapp.generated.resources.display_style_bento
import showcaseapp.composeapp.generated.resources.display_style_cube
import showcaseapp.composeapp.generated.resources.display_style_fade
import showcaseapp.composeapp.generated.resources.display_style_frame_wall
import showcaseapp.composeapp.generated.resources.display_style_reveal
import showcaseapp.composeapp.generated.resources.display_style_slide
import showcaseapp.composeapp.generated.resources.display_style_square
import showcaseapp.composeapp.generated.resources.display_style_waterfall
import showcaseapp.composeapp.generated.resources.interval
import showcaseapp.composeapp.generated.resources.second
import showcaseapp.composeapp.generated.resources.show_time_and_date
import showcaseapp.composeapp.generated.resources.showcase_general
import showcaseapp.composeapp.generated.resources.showcase_style
import showcaseapp.composeapp.generated.resources.sort_rule
import showcaseapp.composeapp.generated.resources.source_preference

const val SHOWCASE_MODE_SLIDE = 0
const val SHOWCASE_MODE_FRAME_WALL = 1
const val SHOWCASE_MODE_FADE = 2
const val SHOWCASE_SCROLL_FADE = 3
const val SHOWCASE_SQUARE = 4
const val SHOWCASE_MODE_SQUARE = SHOWCASE_SQUARE
const val SHOWCASE_MODE_CALENDER = 5

const val SHOWCASE_MODE_CUBE = 6
const val SHOWCASE_MODE_REVEAL = 7
const val SHOWCASE_MODE_CAROUSEL = 8
const val SHOWCASE_MODE_BENTO = 9
const val SHOWCASE_MODE_WATERFALL = 10

val settingsStyleList = listOf(
    ShowcaseMode.Slide,
    ShowcaseMode.Fade,
    ShowcaseMode.FrameWall,
    ShowcaseMode.Calender,
    ShowcaseMode.Bento,
    ShowcaseMode.Waterfall,
    ShowcaseMode.Square
)

@Composable
fun ShowcaseSettings(
    settings: Settings = Settings.getDefaultInstance(),
    generalPreference: GeneralPreference = GeneralPreference(SYSTEM_DEFAULT, FOLLOW_SYSTEM),
    onSettingChanged: (Settings) -> Unit,
    onGeneralSettingChanged: (GeneralPreference) -> Unit,
    onOpenAiProviders: (() -> Unit)? = null,
    onOpenAiCreations: (() -> Unit)? = null,
) {

    val styleList = remember {
        settingsStyleList
    }

    Column {

      TextTitleMedium(modifier = Modifier.padding(8.dp), text = stringResource(Res.string.showcase_style))

        Surface(
            modifier = Modifier.padding(horizontal = 18.dp),
            tonalElevation = 2.dp,
            shadowElevation = 2.dp,
            shape = RoundedCornerShape(16.dp)
        ) {
            Column {
                CheckItem(
                    Icons.Outlined.Style,
                    ShowcaseMode.fromValue(settings.showcaseMode).toPairWithResString(),
                    stringResource(Res.string.showcase_style),
                    styleList.map { it.toPairWithResString() },
                    onCheck = {
                        onSettingChanged(settings.copy(showcaseMode = it.first))
                    }
                )

                when (settings.showcaseMode) {
                    SHOWCASE_MODE_SLIDE -> {

                        SlideModeView(settings.slideMode) { key, value ->
                            val slideMode = when (key) {
                                DisplayMode.key -> {
                                    settings.slideMode.copy(
                                        displayMode = value as Int
                                    )
                                }

                                Orientation.key -> {
                                    settings.slideMode.copy(
                                        orientation = value as Int
                                    )
                                }

                                AutoPlayDuration.key -> {
                                    settings.slideMode.copy(
                                        intervalTime = value as Int
                                    )
                                }

                                IntervalTimeUnit.key -> {
                                    settings.slideMode.copy(
                                        intervalTimeUnit = value as Int
                                    )
                                }

                                ShowTimeProgressIndicator.key -> {
                                    settings.slideMode.copy(
                                        showTimeProgressIndicator = value as Boolean
                                    )
                                }

                                AI_IMAGE_SUMMARY_KEY -> settings.slideMode.copy(enableAiImageSummary = value as Boolean)
                                ShowContentMetaInfo.key -> {
                                    settings.slideMode.copy(
                                        showContentMetaInfo = value as Boolean
                                    )
                                }

                                DUO_FOLD_RETREAT_KEY -> settings.slideMode.copy(duoFoldRetreat = value as Boolean)
                                SlideEffect.key -> {
                                    settings.slideMode.copy(
                                        effect = value as Int
                                    )
                                }

                                SortRule.key -> {
                                    settings.slideMode.copy(
                                        sortRule = value as Int
                                    )
                                }

                                else -> {
                                    settings.slideMode
                                }
                            }
                            onSettingChanged(
                                settings.copy(
                                    slideMode = slideMode
                                )
                            )
                        }

                        SwitchItem(
                            Icons.Outlined.AccessTime,
                            check = settings.showTimeAndDate,
                            desc = stringResource(Res.string.show_time_and_date),
                            onCheck = {
                                onSettingChanged(settings.copy(showTimeAndDate = it))
                            }
                        )
                    }


                    SHOWCASE_MODE_FRAME_WALL -> {

                        FrameWallModeView(settings.frameWallMode) { key, value ->
                            val frameWallMode = when (key) {
                                FrameWallMode.key -> {
                                    settings.frameWallMode.copy(
                                        frameStyle = value as Int
                                    )
                                }

                                MatrixSize.Row -> {
                                    settings.frameWallMode.copy(
                                        matrixSizeRow = value as Int
                                    )
                                }

                                MatrixSize.Column -> {
                                    settings.frameWallMode.copy(
                                        matrixSizeColumn = value as Int
                                    )
                                }

                                Interval.key -> {
                                    settings.frameWallMode.copy(
                                        interval = value as Int
                                    )
                                }

                                DisplayMode.key -> {
                                    settings.frameWallMode.copy(
                                        displayMode = value as Int
                                    )
                                }

                                else -> {
                                    settings.frameWallMode
                                }
                            }

                            onSettingChanged(
                                settings.copy(
                                    frameWallMode = frameWallMode
                                )
                            )

                        }

                        SwitchItem(
                            Icons.Outlined.AccessTime,
                            check = settings.showTimeAndDate,
                            desc = stringResource(Res.string.show_time_and_date),
                            onCheck = {
                                onSettingChanged(settings.copy(showTimeAndDate = it))
                            }
                        )

                    }

                    SHOWCASE_MODE_FADE -> {

                        FadeModeView(settings.fadeMode) { key, value ->

                            val fadeMode = when (key) {
                                DisplayMode.key -> {
                                    settings.fadeMode.copy(
                                        displayMode = value as Int
                                    )
                                }

                                AutoPlayDuration.key -> {
                                    settings.fadeMode.copy(
                                        intervalTime = value as Int
                                    )
                                }

                                IntervalTimeUnit.key -> {
                                    settings.fadeMode.copy(
                                        intervalTimeUnit = value as Int
                                    )
                                }

                                ShowTimeProgressIndicator.key -> {
                                    settings.fadeMode.copy(
                                        showTimeProgressIndicator = value as Boolean
                                    )
                                }

                                AI_IMAGE_SUMMARY_KEY -> settings.fadeMode.copy(enableAiImageSummary = value as Boolean)
                                ShowContentMetaInfo.key -> {
                                    settings.fadeMode.copy(
                                        showContentMetaInfo = value as Boolean
                                    )
                                }

                                SortRule.key -> {
                                    settings.fadeMode.copy(
                                        sortRule = value as Int
                                    )
                                }

                                else -> {
                                    settings.fadeMode
                                }
                            }

                            onSettingChanged(
                                settings.copy(
                                    fadeMode = fadeMode
                                )
                            )
                        }

                        SwitchItem(
                            Icons.Outlined.AccessTime,
                            check = settings.showTimeAndDate,
                            desc = stringResource(Res.string.show_time_and_date),
                            onCheck = {
                                onSettingChanged(settings.copy(showTimeAndDate = it))
                            }
                        )
                    }

                    SHOWCASE_MODE_CALENDER -> {
                        CalenderView(settings.calenderMode) { key, value ->

                            val calenderModeBuilder = when (key) {

                                DisplayMode.key -> {
                                    settings.calenderMode.copy(displayMode = value as Int)
                                }

                                AutoPlay.key -> {
                                    settings.calenderMode.copy(autoPlay = value as Boolean)
                                }

                                AutoPlayDuration.key -> {
                                    settings.calenderMode.copy(intervalTime = value as Int)
                                }

                                IntervalTimeUnit.key -> {
                                    settings.calenderMode.copy(intervalTimeUnit = value as Int)
                                }

                                AI_IMAGE_SUMMARY_KEY -> settings.calenderMode.copy(enableAiImageSummary = value as Boolean)
                                ShowContentMetaInfo.key -> {
                                    settings.calenderMode.copy(showContentMetaInfo = value as Boolean)
                                }

                                else -> {
                                    settings.calenderMode
                                }
                            }
                            onSettingChanged(settings.copy(calenderMode = calenderModeBuilder))
                        }

                        SwitchItem(
                            Icons.Outlined.AccessTime,
                            check = settings.showTimeAndDate,
                            desc = stringResource(Res.string.show_time_and_date),
                            onCheck = { onSettingChanged(settings.copy(showTimeAndDate = it)) }
                        )
                    }

                    SHOWCASE_MODE_BENTO -> {
                        BentoView(settings.bentoMode) { key, value ->

                            val bentoMode = when (key) {

                                Interval.key -> {
                                    settings.bentoMode.copy(interval = value as Int)
                                }

                                BentoStyle.key -> {
                                    settings.bentoMode.copy(bentoStyle = value as Int)
                                }

                                else -> {
                                    settings.bentoMode
                                }
                            }
                            onSettingChanged(settings.copy(bentoMode = bentoMode))
                        }
                    }

                    SHOWCASE_MODE_SQUARE -> {
                        SquareView(settings.squareMode) { squareMode ->
                            onSettingChanged(settings.copy(squareMode = squareMode))
                        }

                        SwitchItem(
                            Icons.Outlined.AccessTime,
                            check = settings.showTimeAndDate,
                            desc = stringResource(Res.string.show_time_and_date),
                            onCheck = { onSettingChanged(settings.copy(showTimeAndDate = it)) }
                        )
                    }

                    SHOWCASE_MODE_WATERFALL -> {
                        WaterfallView(settings.waterfallMode) { waterfallMode ->
                            onSettingChanged(settings.copy(waterfallMode = waterfallMode))
                        }

                        SwitchItem(
                            Icons.Outlined.AccessTime,
                            check = settings.showTimeAndDate,
                            desc = stringResource(Res.string.show_time_and_date),
                            onCheck = { onSettingChanged(settings.copy(showTimeAndDate = it)) }
                        )
                    }

                    else -> {

                    }
                }

                CheckItem(
                    Icons.AutoMirrored.Outlined.Sort,
                    SortRule.fromValue(settings.sortRule).toPairWithResString(),
                    stringResource(Res.string.sort_rule),
                    listOf(
                        SortRule.Random.toPairWithResString(),
                        SortRule.NameAsc.toPairWithResString(),
                        SortRule.NameDesc.toPairWithResString(),
                        SortRule.DateAsc.toPairWithResString(),
                        SortRule.DateDesc.toPairWithResString()
                    ),
                    onCheck = {
                        onSettingChanged(settings.copy(sortRule = it.first))
                    }
                )
            }


        }

        TextTitleMedium(modifier = Modifier.padding(8.dp), text = stringResource(Res.string.showcase_general))

        Surface(
            modifier = Modifier.padding(horizontal = 18.dp),
            tonalElevation = 2.dp,
            shadowElevation = 2.dp,
            shape = RoundedCornerShape(16.dp)
        ) {
            GeneralView(generalPreference) { key, value ->
                when (key) {
                    GeneralPreferenceKey.Language -> {
                        onGeneralSettingChanged(generalPreference.copy(language = value as Int))
                    }

                    GeneralPreferenceKey.AnonymousUsage -> {
                        val enabled = value as Boolean
                        onGeneralSettingChanged(
                            generalPreference.copy(
                                anonymousUsage = enabled,
                                anonymousUsageConsentVersion = ANONYMOUS_USAGE_CONSENT_VERSION
                            )
                        )
                    }

                    GeneralPreferenceKey.DarkMode -> {
                        onGeneralSettingChanged(generalPreference.copy(darkMode = value as Int))
                    }

                    GeneralPreferenceKey.ThemeStyle -> {
                        onGeneralSettingChanged(
                            generalPreference.copy(
                                themeStyle = AppThemeStyle.fromValue(value as Int).value
                            )
                        )
                    }

                    GeneralPreferenceKey.CacheSize -> {
                        onGeneralSettingChanged(generalPreference.copy(cacheSize = value as Int))
                    }

                    GeneralPreferenceKey.AutoCheckUpdate -> {
                        onGeneralSettingChanged(generalPreference.copy(autoCheckUpdate = value as Boolean))
                    }
                }
            }
        }

        if (ConfigSync.isSupported) {
            TextTitleMedium(modifier = Modifier.padding(8.dp), text = stringResource(Res.string.cloud_sync_title))
            CloudSyncSettings()
        }

        if (aiFeaturesAvailable(isWeb())) {
            TextTitleMedium(
                modifier = Modifier.padding(8.dp),
                text = stringResource(Res.string.ai_configuration_title),
            )
            AiClientSettings(
                onOpenProviders = onOpenAiProviders,
                onOpenCreations = onOpenAiCreations,
            )
        }

       TextTitleMedium(modifier = Modifier.padding(8.dp), text = stringResource(Res.string.source_preference))

        Surface(
            modifier = Modifier.padding(horizontal = 18.dp),
            tonalElevation = 2.dp,
            shadowElevation = 2.dp,
            shape = RoundedCornerShape(16.dp)
        ) {
            SourcePreferenceView(settings) { key, value ->
                val settingsBuilder = when (key) {
                    SourcePreferenceItem.RecursiveDir -> {
                        settings.copy(recursiveDirContent = value as Boolean)
                    }

                    SourcePreferenceItem.AutoRefresh -> {
                        settings.copy(autoRefresh = value as Boolean)
                    }

                    SourcePreferenceItem.AutoOpenLatestSource -> {
                        settings.copy(autoOpenLatestSource = value as Boolean)
                    }

                    SourcePreferenceItem.SupportVideo -> {
                        settings.copy(supportVideo = value as Boolean)
                    }

                    SourcePreferenceItem.AutoFullScreen -> {
                        settings.copy(autoFullScreen = value as Boolean)
                    }

                    else -> {
                        settings
                    }
                }
                onSettingChanged(settingsBuilder)
            }
        }

        TextTitleMedium(modifier = Modifier.padding(8.dp), text = stringResource(Res.string.about))

        Surface(
            modifier = Modifier.padding(horizontal = 18.dp),
            tonalElevation = 2.dp,
            shadowElevation = 2.dp,
            shape = RoundedCornerShape(16.dp)
        ) {
            AboutView(
                generalPreference = generalPreference,
                onGeneralSettingChanged = onGeneralSettingChanged
            )
        }

    }
}


sealed class ShowcaseMode(type: Int, title: String, resString: StringResource) :
    Select<Int>(type, title, resString) {
    data object Slide : ShowcaseMode(SHOWCASE_MODE_SLIDE, "Slide", Res.string.display_style_slide)
    data object FrameWall :
        ShowcaseMode(SHOWCASE_MODE_FRAME_WALL, "Frame wall", Res.string.display_style_frame_wall)

    data object Fade : ShowcaseMode(SHOWCASE_MODE_FADE, "Fade", Res.string.display_style_fade)
    data object Calender :
        ShowcaseMode(SHOWCASE_MODE_CALENDER, "Calender", Res.string.display_style_calender)

    data object Cube :
        ShowcaseMode(SHOWCASE_MODE_CUBE, "Cube", Res.string.display_style_cube)

    data object Reveal :
        ShowcaseMode(SHOWCASE_MODE_REVEAL, "Reveal", Res.string.display_style_reveal)

    data object Carousel :
        ShowcaseMode(SHOWCASE_MODE_CAROUSEL, "Carousel", Res.string.display_style_carousel)

    data object Bento :
        ShowcaseMode(SHOWCASE_MODE_BENTO, "Bento", Res.string.display_style_bento)

    data object Square :
        ShowcaseMode(SHOWCASE_MODE_SQUARE, "Square", Res.string.display_style_square)

    data object Waterfall :
        ShowcaseMode(SHOWCASE_MODE_WATERFALL, "Waterfall", Res.string.display_style_waterfall)

    companion object {
        const val key: String = "ShowcaseMode"
        fun fromValue(type: Int): ShowcaseMode {
            return when (type) {
                SHOWCASE_MODE_SLIDE -> Slide
                SHOWCASE_MODE_FRAME_WALL -> FrameWall
                SHOWCASE_MODE_FADE -> Fade
                SHOWCASE_MODE_CALENDER -> Calender
                SHOWCASE_MODE_CUBE -> Cube
                SHOWCASE_MODE_REVEAL -> Reveal
                SHOWCASE_MODE_CAROUSEL -> Carousel
                SHOWCASE_MODE_BENTO -> Bento
                SHOWCASE_MODE_SQUARE -> Square
                SHOWCASE_MODE_WATERFALL -> Waterfall
                else -> Slide
            }
        }
    }
}

fun getModeName(mode: Int): String {
    return when (mode) {
        SHOWCASE_MODE_SLIDE -> ShowcaseMode.Slide.title
        SHOWCASE_MODE_FRAME_WALL -> ShowcaseMode.FrameWall.title
        SHOWCASE_MODE_FADE -> ShowcaseMode.Fade.title
        SHOWCASE_MODE_BENTO -> ShowcaseMode.Bento.title
        SHOWCASE_MODE_CALENDER -> ShowcaseMode.Calender.title
        SHOWCASE_MODE_CUBE -> ShowcaseMode.Cube.title
        SHOWCASE_MODE_REVEAL -> ShowcaseMode.Reveal.title
        SHOWCASE_MODE_CAROUSEL -> ShowcaseMode.Carousel.title
        SHOWCASE_MODE_SQUARE -> ShowcaseMode.Square.title
        SHOWCASE_MODE_WATERFALL -> ShowcaseMode.Waterfall.title

        else -> {
            "Unknown"
        }
    }
}
